import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { FC, ReactNode } from 'react';
import { isAxiosError } from 'axios';
import { useMatch } from 'react-router-dom';
import {
  NODON_GENERATION_PROJECT_MATCH_PATH,
  NODON_SHARED_PROJECT_MATCH_PATH,
} from '@shared/constants';
import type { MapCameraState } from '@shared/models/design/map-render.model';
import { waitUntilDefined } from '@shared/helpers/promise.helpers';
import { REPORT_PDF_COVER_MAP_CAPTURE_WAIT_MS } from '@shared/helpers/report-pdf-generation.helpers';
import { tryCatchAwaitable } from '@shared/helpers/utils.helpers';
import { useDebouncedSnackbar } from '@client/hooks/use-snackbar.hooks';
import { useErrorHandler } from '@client/hooks/use-error-handler.hooks';
import {
  decodeImageUrl,
  logImageDataUrlToConsole,
} from '@client/helpers/image-decode.helpers';
import {
  getCoverStatusAfterDeleteFailure,
  isCoverSettledStatus,
  type ReportCoverImageStatus,
} from '@client/helpers/report-cover-image-status.helpers';
import { setReportCoverSettled } from '@client/helpers/report-pdf-ready.helpers';
import { getProject } from '@client/store/project/project.hook';
import { useUser } from '@client/hooks/use-user.hooks';
import { useProjectStateStore } from '@client/store/project/project.store';
import { UpdateClientAction } from '@client/store/store.model';
import { requestPhotorealisticRender } from '@features/design/api/design-ai.api';
import type { GenerateImageDialogValue } from '@features/design/components/GenerateImageDialog';
import {
  logRenderPromptToConsole,
  mergeClientPrompt,
  shouldLogMapRenderDebug,
} from '@features/design/helpers/design-ai-render-prompt.helpers';
import { getBuildingDesignFromProjectOrBuilding } from '@shared/helpers/design/design.helpers';
import { sourceTypeNeedsScreenshot } from '@shared/helpers/design/map-render.helpers';
import { buildReportCoverRenderRequest } from '@shared/helpers/report-cover-image.helpers';
import {
  createReportCoverImage,
  deleteReportCoverImage,
  fetchReportCoverImage,
} from './api/report-cover-image.api';

export type { ReportCoverImageStatus };

/** Keep the open project's {@link Project.cover_image_url} in sync without a project PUT. */
const patchLocalCoverImageUrl = (coverImageUrl: string | null): void => {
  const project = getProject();
  useProjectStateStore.getState().updateProjectLocally({
    action: UpdateClientAction.Update,
    itemOrId: { ...project, cover_image_url: coverImageUrl },
  });
};

type ReportCoverMapCapture = {
  captureScreenshot: () => Promise<string>;
  captureVisibleFrame: () => Promise<string>;
  getCamera: () => MapCameraState | undefined;
};

type ReportCoverImageContextValue = {
  status: ReportCoverImageStatus;
  imageUrl: string | undefined;
  isCaptureReady: boolean;
  getCamera: () => MapCameraState | undefined;
  generate: (
    value: GenerateImageDialogValue,
    camera: MapCameraState,
  ) => Promise<void>;
  remove: () => Promise<void>;
  registerMapCapture: (capture: ReportCoverMapCapture | undefined) => void;
  /**
   * PNG of the live 3D cover for PDF export. Undefined when a stored Gemini
   * image exists or the map cannot be read.
   */
  captureCoverMapForPdf: () => Promise<string | undefined>;
};

const ReportCoverImageContext = createContext<
  ReportCoverImageContextValue | undefined
>(undefined);

/**
 * Shared cover-image state for the Admin Area actions and both cover-page instances
 * (main pane + thumbnail).
 */
export const ReportCoverImageProvider: FC<{
  projectId: number;
  children: ReactNode;
}> = ({ projectId, children }) => {
  const generationMatch = useMatch(NODON_GENERATION_PROJECT_MATCH_PATH);
  const sharedMatch = useMatch(NODON_SHARED_PROJECT_MATCH_PATH);
  const generationKey = generationMatch?.params.generationKey;
  const sharingKey = sharedMatch?.params.sharingKey;

  const [status, setStatus] = useState<ReportCoverImageStatus>('loading');
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
  const [isCaptureReady, setIsCaptureReady] = useState(false);
  const imageUrlRef = useRef<string | undefined>(undefined);
  const captureRef = useRef<ReportCoverMapCapture | undefined>(undefined);
  const loadIdRef = useRef(0);
  const enqueueSnackbar = useDebouncedSnackbar();
  const errorHandler = useErrorHandler();
  const user = useUser();

  const revokeImageUrl = useCallback(() => {
    if (imageUrlRef.current) {
      URL.revokeObjectURL(imageUrlRef.current);
      imageUrlRef.current = undefined;
    }
    setImageUrl(undefined);
  }, []);

  /**
   * Decode the blob before publishing `ready` so PDF capture does not screenshot
   * an empty cover slot. No-ops if a newer load has started.
   */
  const publishDecodedBlob = useCallback(
    async (blob: Blob, loadId: number): Promise<boolean> => {
      const url = URL.createObjectURL(blob);
      const decoded = await tryCatchAwaitable(decodeImageUrl(url));
      if (loadId !== loadIdRef.current) {
        URL.revokeObjectURL(url);
        return false;
      }
      if (!decoded.ok) {
        URL.revokeObjectURL(url);
        revokeImageUrl();
        errorHandler(decoded.error);
        // Row exists but bytes did not decode — settle as error so PDF capture
        // can proceed with an empty cover slot instead of hanging.
        setStatus('error');
        return false;
      }
      revokeImageUrl();
      imageUrlRef.current = url;
      setImageUrl(url);
      setStatus('ready');
      return true;
    },
    [errorHandler, revokeImageUrl],
  );

  const loadStoredImage = useCallback(async () => {
    const project = getProject();
    if (project.id === projectId && !project.cover_image_url) {
      loadIdRef.current += 1;
      revokeImageUrl();
      setStatus('missing');
      return;
    }
    const loadId = ++loadIdRef.current;
    setStatus('loading');
    const result = await tryCatchAwaitable(
      fetchReportCoverImage({ projectId, generationKey, sharingKey }),
    );
    if (loadId !== loadIdRef.current) return;
    if (!result.ok) {
      // Failed GET (including 404) is not absence — absence is HTTP 204 above.
      errorHandler(result.error);
      revokeImageUrl();
      setStatus('error');
      return;
    }
    // 204 No Content: no stored cover; use the live 3D view.
    if (!result.value) {
      revokeImageUrl();
      setStatus('missing');
      return;
    }
    await publishDecodedBlob(result.value, loadId);
  }, [
    errorHandler,
    generationKey,
    projectId,
    publishDecodedBlob,
    revokeImageUrl,
    sharingKey,
  ]);

  useEffect(() => {
    void loadStoredImage();
    return () => {
      loadIdRef.current += 1;
      if (imageUrlRef.current) {
        URL.revokeObjectURL(imageUrlRef.current);
        imageUrlRef.current = undefined;
      }
    };
  }, [loadStoredImage]);

  useEffect(() => {
    setReportCoverSettled(isCoverSettledStatus(status));
  }, [status]);

  useEffect(() => () => setReportCoverSettled(false), []);

  const registerMapCapture = useCallback(
    (capture: ReportCoverMapCapture | undefined) => {
      captureRef.current = capture;
      setIsCaptureReady(Boolean(capture));
    },
    [],
  );

  const generate = useCallback(
    async (value: GenerateImageDialogValue, camera: MapCameraState) => {
      const capture = captureRef.current;
      if (!capture) {
        enqueueSnackbar('The cover map is not ready yet', {
          variant: 'warning',
        });
        return;
      }

      const loadId = ++loadIdRef.current;
      setStatus('generating');
      const result = await tryCatchAwaitable(
        (async () => {
          const screenshot = sourceTypeNeedsScreenshot(value.sourceType)
            ? await capture.captureScreenshot()
            : undefined;
          const project = getProject();
          const mergedPrompt = mergeClientPrompt({
            systemPrompt: value.systemPrompt,
            userPrompt: value.userPrompt,
            buildingDesign: getBuildingDesignFromProjectOrBuilding(project),
          });
          if (shouldLogMapRenderDebug(user)) {
            if (screenshot) {
              logImageDataUrlToConsole('Map screenshot sent to AI', screenshot);
            }
            logRenderPromptToConsole(mergedPrompt);
          }
          const payload = buildReportCoverRenderRequest({
            project,
            camera,
            sourceType: value.sourceType,
            screenshot,
            prompt: mergedPrompt,
            ai: { provider: value.provider, model: value.model },
          });
          const rendered = await requestPhotorealisticRender(payload);
          if (!rendered.image) {
            throw new Error('Image generation did not return an image');
          }
          const created = await createReportCoverImage(projectId, {
            image: rendered.image,
          });
          patchLocalCoverImageUrl(created.cover_image_url);
          return fetchReportCoverImage({
            projectId,
            generationKey,
            sharingKey,
          });
        })(),
      );

      if (loadId !== loadIdRef.current) return;
      if (!result.ok) {
        errorHandler(result.error);
        // Re-fetch so a failed regenerate does not hide an image still in the DB.
        await loadStoredImage();
        return;
      }
      if (!result.value) {
        errorHandler(new Error('Cover image was not stored after generate'));
        await loadStoredImage();
        return;
      }

      const published = await publishDecodedBlob(result.value, loadId);
      if (loadId !== loadIdRef.current) return;
      if (!published) return;
      enqueueSnackbar('Cover image created', { variant: 'success' });
    },
    [
      enqueueSnackbar,
      errorHandler,
      generationKey,
      loadStoredImage,
      projectId,
      publishDecodedBlob,
      sharingKey,
      user,
    ],
  );

  const remove = useCallback(async () => {
    const loadId = ++loadIdRef.current;
    setStatus('removing');
    const result = await tryCatchAwaitable(deleteReportCoverImage(projectId));
    if (loadId !== loadIdRef.current) return;
    if (!result.ok) {
      const httpStatus = isAxiosError(result.error)
        ? result.error.response?.status
        : undefined;
      const nextStatus = getCoverStatusAfterDeleteFailure(httpStatus);
      if (nextStatus === 'missing') {
        revokeImageUrl();
        patchLocalCoverImageUrl(null);
      } else {
        errorHandler(result.error);
      }
      setStatus(nextStatus);
      return;
    }
    revokeImageUrl();
    patchLocalCoverImageUrl(null);
    setStatus('missing');
    enqueueSnackbar('Cover image removed', { variant: 'success' });
  }, [enqueueSnackbar, errorHandler, projectId, revokeImageUrl]);

  const getCamera = useCallback(() => captureRef.current?.getCamera(), []);

  const captureCoverMapForPdf = useCallback(async (): Promise<
    string | undefined
  > => {
    if (status === 'ready' && imageUrl) return undefined;
    const capture = await waitUntilDefined(() => captureRef.current, {
      timeoutMs: REPORT_PDF_COVER_MAP_CAPTURE_WAIT_MS,
      intervalMs: 50,
    });
    if (!capture) return undefined;
    const result = await tryCatchAwaitable(capture.captureVisibleFrame());
    if (!result.ok) return undefined;
    if (!result.value.startsWith('data:image')) return undefined;
    return result.value;
  }, [imageUrl, status]);

  const value = useMemo<ReportCoverImageContextValue>(
    () => ({
      status,
      imageUrl,
      isCaptureReady,
      getCamera,
      generate,
      remove,
      registerMapCapture,
      captureCoverMapForPdf,
    }),
    [
      captureCoverMapForPdf,
      generate,
      getCamera,
      imageUrl,
      isCaptureReady,
      registerMapCapture,
      remove,
      status,
    ],
  );

  return (
    <ReportCoverImageContext.Provider value={value}>
      {children}
    </ReportCoverImageContext.Provider>
  );
};

/**
 * Cover-image state for the report view. Must be used under
 * {@link ReportCoverImageProvider}.
 */
export const useReportCoverImage = (): ReportCoverImageContextValue => {
  const value = useContext(ReportCoverImageContext);
  if (!value) {
    throw new Error(
      'useReportCoverImage must be used within ReportCoverImageProvider',
    );
  }
  return value;
};
